import type { DATE_FORMATS } from '@atnt/shared';

/**
 * Value/date formatting for live widgets, pins and tables (docs/design/02-constructor.md §4.5, §6.6, §12.1 Q22).
 * Numbers: sensor decimals (or auto: ≤ 2 decimals, trailing zeros trimmed → "854.6", "1.8", "0.45"); null → "—".
 */

/** Decimal separator per UI language. Uzbek follows the design ("854.6"); change here if the owner picks "0,45". */
export const DECIMAL_SEPARATOR: Record<string, string> = { 'uz-Latn': '.', 'uz-Cyrl': '.', ru: ',', en: '.' };
/** Thousands separator: narrow no-break space ("12 543"). */
export const GROUP_SEPARATOR = ' ';
export const NO_VALUE = '—';
export const DEFAULT_TIME_ZONE = 'Asia/Tashkent';

export const decimalSeparator = (locale: string | undefined) =>
  DECIMAL_SEPARATOR[locale ?? ''] ?? DECIMAL_SEPARATOR[(locale ?? '').split('-')[0] ?? ''] ?? '.';

export interface FormatNumberOptions {
  /** fixed decimals (sensor precision / binding override); `null`/`undefined` = auto */
  decimals?: number | null;
  locale?: string;
  /** thousands grouping: `auto` groups numbers with ≥ 5 integer digits */
  group?: boolean | 'auto';
}

export function formatNumber(value: number | null | undefined, { decimals, locale, group = 'auto' }: FormatNumberOptions = {}): string {
  if (value == null || !Number.isFinite(value)) return NO_VALUE;
  let text = decimals != null ? value.toFixed(Math.max(0, Math.min(10, decimals))) : (Math.round(value * 100) / 100).toFixed(2).replace(/\.?0+$/, '');
  let negative = text.startsWith('-');
  if (negative) text = text.slice(1);
  if (/^[0.]+$/.test(text)) negative = false; // no "−0"
  const [intPart = '0', frac] = text.split('.');
  const grouped = group === true || (group === 'auto' && intPart.length >= 5) ? intPart.replace(/\B(?=(\d{3})+(?!\d))/g, GROUP_SEPARATOR) : intPart;
  return `${negative ? '−' : ''}${grouped}${frac ? decimalSeparator(locale) + frac : ''}`;
}

/** "854.6 m", "0.4°C" — no space before units that start with "°" (§6.6). */
export const unitSeparator = (unit: string) => (unit.startsWith('°') ? '' : ' ');

export function withUnit(text: string, unit: string | null | undefined): string {
  if (!unit || text === NO_VALUE) return text;
  return `${text}${unitSeparator(unit)}${unit}`;
}

export const formatCount = (n: number | null | undefined, locale?: string) => formatNumber(n ?? 0, { decimals: 0, group: true, locale });

/* ───────────────────────── dates & times (reservoir time zone) ───────────────────────── */

const MONTHS: Record<string, readonly string[]> = {
  'uz-Latn': ['yanvar', 'fevral', 'mart', 'aprel', 'may', 'iyun', 'iyul', 'avgust', 'sentabr', 'oktabr', 'noyabr', 'dekabr'],
  'uz-Cyrl': ['январ', 'феврал', 'март', 'апрел', 'май', 'июн', 'июл', 'август', 'сентябр', 'октябр', 'ноябр', 'декабр'],
  ru: ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня', 'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'],
  en: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
};

interface ZonedParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

const partFormatters = new Map<string, Intl.DateTimeFormat>();
function formatterFor(timeZone: string) {
  let f = partFormatters.get(timeZone);
  if (!f) {
    try {
      f = new Intl.DateTimeFormat('en-GB', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hourCycle: 'h23',
      });
    } catch {
      f = new Intl.DateTimeFormat('en-GB', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23' });
    }
    partFormatters.set(timeZone, f);
  }
  return f;
}

export function zonedParts(ms: number, timeZone = DEFAULT_TIME_ZONE): ZonedParts {
  const out: Record<string, number> = {};
  for (const p of formatterFor(timeZone).formatToParts(ms)) if (p.type !== 'literal') out[p.type] = Number(p.value);
  return { year: out.year ?? 1970, month: out.month ?? 1, day: out.day ?? 1, hour: (out.hour ?? 0) % 24, minute: out.minute ?? 0, second: out.second ?? 0 };
}

const pad = (n: number) => String(n).padStart(2, '0');

export function formatClock(ms: number, { seconds = true, timeZone = DEFAULT_TIME_ZONE }: { seconds?: boolean; timeZone?: string } = {}) {
  const p = zonedParts(ms, timeZone);
  return seconds ? `${pad(p.hour)}:${pad(p.minute)}:${pad(p.second)}` : `${pad(p.hour)}:${pad(p.minute)}`;
}

export function formatDate(
  ms: number,
  format: (typeof DATE_FORMATS)[number],
  { locale = 'uz-Latn', timeZone = DEFAULT_TIME_ZONE }: { locale?: string; timeZone?: string } = {},
) {
  const p = zonedParts(ms, timeZone);
  if (format === 'dd.MM.yyyy') return `${pad(p.day)}.${pad(p.month)}.${p.year}`;
  const months = MONTHS[locale] ?? MONTHS[locale.split('-')[0] ?? ''] ?? MONTHS['uz-Latn']!;
  return `${p.day} ${months[p.month - 1]} ${p.year}`;
}

/** "27.10.2026 10:53" */
export function formatDateTime(ts: string | number | null | undefined, { timeZone = DEFAULT_TIME_ZONE, seconds = false }: { timeZone?: string; seconds?: boolean } = {}) {
  if (ts == null) return NO_VALUE;
  const ms = typeof ts === 'number' ? ts : Date.parse(ts);
  if (!Number.isFinite(ms)) return NO_VALUE;
  const p = zonedParts(ms, timeZone);
  return `${pad(p.day)}.${pad(p.month)}.${p.year} ${pad(p.hour)}:${pad(p.minute)}${seconds ? `:${pad(p.second)}` : ''}`;
}

/** Short axis labels: "27.10" (days) or "14:30" (within a day). */
export function formatAxisTime(ms: number, spanMs: number, timeZone = DEFAULT_TIME_ZONE) {
  const p = zonedParts(ms, timeZone);
  return spanMs > 36 * 3600_000 ? `${pad(p.day)}.${pad(p.month)}` : `${pad(p.hour)}:${pad(p.minute)}${spanMs < 15 * 60_000 ? `:${pad(p.second)}` : ''}`;
}
